import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SDLogo, LinkedInIcon, Spinner } from '@/components/atoms';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileState } from '@/contexts/ProfileContext';
import { auth as authApi, profile as profileApi } from '@/lib/api';
import { trackEvent, EVENTS } from '@/lib/analytics';

export default function Welcome() {
  const { user, refresh } = useAuth();
  const { savedProfile, analysis, reload } = useProfileState();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [info, setInfo] = useState(null);

  useEffect(() => { setError(null); setInfo(null); }, [mode]);

  const routeAfterAuth = async () => {
    // Refresh auth state (populates the user in context). We deliberately do NOT await
    // `reload()` here: (a) it uses a stale closure captured from the previous render,
    // and (b) ProfileProvider's own useEffect will auto-reload when `user` changes.
    // Instead we fetch profile+analysis directly with the FRESH session token and
    // route from those responses. The target page will consume the (still-hydrating)
    // context as it becomes available.
    await refresh();
    const { profile: p, analysis: a } = await import('@/lib/api');
    const [prg, prf] = await Promise.all([
      a.latest().catch(() => ({ analysis: null })),
      p.get().catch(() => ({ profile: null })),
    ]);
    // Always clear busy right before navigating so the submit button stops spinning
    // even if the router transition takes a beat.
    setBusy(false);
    if (prg?.analysis) navigate('/dashboard', { replace: true });
    else if (prf?.profile?.pivot_type) navigate('/onboarding/review', { replace: true });
    else if (prf?.profile?.name) navigate('/onboarding/confirm', { replace: true });
    else navigate('/onboarding/connect', { replace: true });
  };

  const handleLinkedIn = async () => {
    setBusy(true);
    try {
      const { url } = await authApi.linkedinUrl();
      window.location.href = url;
    } catch (err) {
      console.warn('[LinkedIn OAuth] linkedinUrl failed:', err?.message || err);
      setError('Could not start LinkedIn sign-in. Try email instead.');
      setBusy(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) { setError('Enter a valid email address.'); return; }
    setBusy(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) { setError('Please enter your name.'); setBusy(false); return; }
        if (password.length < 8) { setError('Password must be at least 8 characters.'); setBusy(false); return; }
        await authApi.register(email, password, name || undefined);
        // Instrumentation: signup event (fire-and-forget)
        try { trackEvent(EVENTS.signup, { method: 'email' }); } catch (_e) { /* no-op */ }
        // Auto-subscribe to Sustainability Decoded on signup — the account
        // creation flow now includes newsletter consent by default (with an
        // in-page notice + unsubscribe link in every email). Fire-and-forget
        // so a transient failure does not block the signup flow.
        profileApi
          .newsletterConsent({ newsletter_opt_in: true, source: 'signup' })
          .catch(() => { /* non-blocking */ });
      } else if (mode === 'forgot') {
        // Sign the user in by email (existing dev-login mechanism), then move OFF
        // the '/' route so Gate does not auto-redirect them to /dashboard before
        // they can set a new password.
        await authApi.devLogin(email, email.split('@')[0]);
        await refresh();
        navigate('/auth/reset-password');
        return;
      } else {
        if (!password) {
          // Email-only quick sign-in (back-compat) — no password set yet
          await authApi.devLogin(email, email.split('@')[0]);
        } else {
          await authApi.login(email, password);
        }
      }
      await routeAfterAuth();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Sign-in failed.';
      setError(msg);
      setBusy(false);
    }
  };

  const resume = () => {
    if (analysis) navigate('/dashboard');
    else if (savedProfile?.pivot_type) navigate('/onboarding/review');
    else if (savedProfile?.name) navigate('/onboarding/confirm');
    else navigate('/onboarding/connect');
  };

  return (
    <div className="min-h-screen bg-sd-ivory flex flex-col">
      <header className="px-8 py-5"><SDLogo variant="brand" /></header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-[920px] w-full grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="heading-mega text-[52px] md:text-[60px] font-semibold text-sd-black mb-6 leading-[1.02]">
              Your sustainability career is worth more than you think.
            </h1>
            <p className="text-[17px] text-sd-mid-gray max-w-[480px] leading-relaxed mb-8">
              SD-Pivot reads your profile, runs it through a sustainability-specific intelligence engine, and shows you the roles, salaries, gaps and actions that move your career forward.
            </p>
            <ul className="space-y-3 text-[15px] text-sd-dark-gray">
              <li className="flex gap-3"><span className="text-sd-primary mt-0.5">✓</span> Specific roles — expected, adjacent, and one surprise</li>
              <li className="flex gap-3"><span className="text-sd-primary mt-0.5">✓</span> Real salary ranges, sourced and calibrated to your level</li>
              <li className="flex gap-3"><span className="text-sd-primary mt-0.5">✓</span> A tactical action plan with milestones, jobs and people to meet</li>
            </ul>
          </div>

          <div className="sd-card p-7">
            {user ? (
              <div className="animate-fade-in">
                <h3 className="text-[22px] font-semibold text-sd-black mb-1">Welcome back, {user.name?.split(' ')[0] || 'there'}</h3>
                <p className="text-sd-mid-gray text-[14px] mb-6">Resume where you left off, or start fresh.</p>
                <button className="sd-btn-primary w-full mb-3" onClick={resume} data-testid="welcome-resume">Resume →</button>
                <button className="sd-btn-secondary w-full" onClick={async () => {
                  if (!window.confirm('Start a new assessment? This clears your saved profile and analysis.')) return;
                  const { profile: pApi } = await import('@/lib/api');
                  await pApi.reset();
                  await reload();
                  navigate('/onboarding/connect');
                }} data-testid="welcome-start-fresh">+ New Assessment</button>
              </div>
            ) : (
              <div className="animate-fade-in">
                <h3 className="text-[22px] font-semibold text-sd-black mb-1">
                  {mode === 'signup' ? 'Create your account' : mode === 'forgot' ? 'Reset your password' : 'Get your career intelligence'}
                </h3>
                <p className="text-sd-mid-gray text-[14px] mb-6">
                  {mode === 'signup'
                    ? 'Sign up to begin. Takes ~6 minutes.'
                    : mode === 'forgot'
                      ? 'Enter your email and we\'ll sign you in so you can set a new password.'
                      : 'Sign in to continue, or sign up.'}
                </p>

                {mode !== 'forgot' && (
                  <>
                    <button onClick={handleLinkedIn} disabled={busy} className="w-full flex items-center justify-center gap-3 rounded-btn px-5 py-3.5 text-white font-semibold text-[14px] mb-3 transition-colors hover:opacity-95" style={{ background: '#0A66C2' }} data-testid="welcome-linkedin">
                      {busy ? <Spinner size={18} color="#FFFFFF" /> : <LinkedInIcon size={20} />}
                      Continue with LinkedIn
                    </button>
                    {mode === 'signup' && (
                      <p
                        className="text-[11.5px] text-sd-mid-gray leading-relaxed text-center mb-4"
                        data-testid="signup-newsletter-consent"
                      >
                        By signing up, you consent to receive email updates from{' '}
                        <strong className="text-sd-black font-medium">Sustainability Decoded</strong>. Unsubscribe anytime.
                      </p>
                    )}

                    <div className="flex items-center gap-3 my-4 text-sd-mid-gray text-[12px]"><span className="flex-1 h-px bg-sd-light-gray" /> or with email <span className="flex-1 h-px bg-sd-light-gray" /></div>
                  </>
                )}

                <form onSubmit={handleEmailSubmit} className="space-y-3">
                  {mode === 'signup' && (
                    <input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="sd-input" required data-testid="welcome-name-input" />
                  )}
                  <input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="sd-input" required data-testid="welcome-email-input" />
                  {mode !== 'forgot' && (
                    <input type="password" placeholder={mode === 'signup' ? 'Choose a password (min 8 chars)' : 'Password (or leave blank for quick sign-in)'} value={password} onChange={(e) => setPassword(e.target.value)} className="sd-input" data-testid="welcome-password-input" />
                  )}
                  {error && <div className="text-[13px] text-sd-error" data-testid="welcome-error">{error}</div>}
                  {info && <div className="text-[13px] text-sd-primary font-medium" data-testid="welcome-info">{info}</div>}
                  {mode === 'signin' && (
                    <div className="text-right">
                      <button type="button" onClick={() => setMode('forgot')} className="text-[12.5px] text-sd-mid-gray hover:text-sd-black underline underline-offset-2" data-testid="welcome-forgot-password">
                        Forgot password?
                      </button>
                    </div>
                  )}
                  <button className="sd-btn-primary w-full" type="submit" disabled={busy} data-testid="welcome-email-submit">
                    {busy
                      ? 'Please wait…'
                      : mode === 'signup'
                        ? 'Create account →'
                        : mode === 'forgot'
                          ? 'Continue to reset →'
                          : 'Sign in →'}
                  </button>
                </form>
                <div className="text-center mt-4 text-[12.5px] text-sd-mid-gray">
                  {mode === 'signup' ? (
                    <>Already have an account? <button className="text-sd-black font-medium underline" onClick={() => setMode('signin')} data-testid="welcome-mode-signin">Sign in</button></>
                  ) : mode === 'forgot' ? (
                    <>Remembered it? <button className="text-sd-black font-medium underline" onClick={() => setMode('signin')} data-testid="welcome-back-to-signin">Back to sign in</button></>
                  ) : (
                    <>New to SD-Pivot? <button className="text-sd-black font-medium underline" onClick={() => setMode('signup')} data-testid="welcome-mode-signup">Create an account</button></>
                  )}
                </div>
                <p className="text-[11px] text-sd-mid-gray mt-5 leading-relaxed text-center">
                  We only use your name and email. We don’t post on your behalf. You can delete your data at any time.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="px-8 py-5 text-center text-[12px] text-sd-mid-gray">
        SD·Pivot · Built for sustainability professionals who want a real career move.
      </footer>
    </div>
  );
}
